import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../../../Include/Header';
import Sidebar from '../../../Include/Sidebar';
import Footer from '../../../Include/Footer';
import Breadcrumb from '../../../../../Components/Breadcrumb/Breadcrumb';
import Loader from '../../../Include/Loader';
import { getViewStockAdjustmentItems } from '../../../../../Store/prosafe/proTransaction/StockAdjustment/getViewStockAdjustmentItems';
import axios from 'axios';
import toast from 'react-hot-toast';
import { V_URL } from '../../../../../BaseUrl';
import moment from 'moment';
import { Printer, ArrowLeft, Download, FileText } from 'lucide-react';

const resolveUnit = (row) => {
    if (!row) return "-";
    if (row.item_id?.unit) {
        if (typeof row.item_id.unit === "object" && row.item_id.unit.name) {
            return row.item_id.unit.name;
        }
        if (typeof row.item_id.unit === "string" && !row.item_id.unit.match(/^[0-9a-fA-F]{24}$/)) {
            return row.item_id.unit;
        }
    }
    if (row.unit) {
        if (typeof row.unit === "object" && row.unit.name) {
            return row.unit.name;
        }
        if (typeof row.unit === "string" && !row.unit.match(/^[0-9a-fA-F]{24}$/)) {
            return row.unit;
        }
    }
    return "-";
};

const ReadOnlyField = ({ label, value, highlightColor }) => (
    <div className="col-12 col-md-3 col-xl-3 mb-3">
        <div className="p-2 border rounded bg-light">
            <small className="text-muted d-block text-uppercase" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                {label}
            </small>
            <div className="font-weight-bold mt-1 text-dark text-truncate" style={{ color: highlightColor }}>
                {value || "-"}
            </div>
        </div>
    </div>
);

const ViewStockAdjustment = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { state: locationData } = useLocation();

    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const voucherId = locationData?._id;

    useEffect(() => {
        if (voucherId) {
            dispatch(getViewStockAdjustmentItems({ formData: { id: voucherId } }));
        }
    }, [voucherId, dispatch]);

    const doc = useSelector((state) => state.getViewStockAdjustmentItems?.user?.data);
    const isLoading = useSelector((state) => state.getViewStockAdjustmentItems?.loading);

    const handleDownloadPdf = async () => {
        if (!voucherId) return;
        try {
            const myurl = `${V_URL}/prosafe/download-stock-adjustment-slip/${voucherId}`;
            const response = await axios.post(
                myurl,
                {},
                {
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("PAY_USER_TOKEN")
                    }
                }
            );

            if (response.data.success && (response.data.data?.pdfUrl || response.data.data?.url)) {
                window.open(response.data.data.pdfUrl || response.data.data.url, "_blank");
                toast.success("Adjustment slip opened successfully");
            } else {
                toast.error(response.data.message || "Failed to generate PDF");
            }
        } catch (error) {
            console.error("PDF download error:", error);
            toast.error(error.response?.data?.message || "Failed to download PDF slip");
        }
    };

    const breadcrumb = [
        { label: "Dashboard", link: "/main-store/user/dashboard" },
        { label: "Stock Adjustment", link: "/main-store/user/stock-adjustment-management" },
        { label: `Voucher Details - ${doc?.voucher_no || ''}`, active: true }
    ];

    const isPlus = doc?.adjustment_type === "PLUS" || doc?.tag_id?.tag_number === 18;
    const items = doc?.items_details || [];
    const totalQty = items.reduce((sum, itm) => sum + (parseFloat(itm.quantity) || 0), 0);
    const totalValuation = items.reduce((sum, itm) => sum + (parseFloat(itm.total_amount) || 0), 0);

    return (
        <div className={`main-wrapper ${isSidebarOpen ? "slide-nav" : ""}`}>
            <Header handleOpen={() => setIsSidebarOpen(!isSidebarOpen)} />
            <Sidebar />
            <div className="page-wrapper">
                <div className="content">
                    <Breadcrumb items={breadcrumb} />

                    {!isLoading && doc ? (
                        <div className="row">
                            <div className="col-12">
                                {/* Header Card */}
                                <div className="card">
                                    <div className="card-header bg-white border-bottom py-3 d-flex flex-wrap justify-content-between align-items-center">
                                        <div>
                                            <h4 className="card-title mb-0 d-flex align-items-center">
                                                <FileText className="me-2 text-primary" size={22} />
                                                Stock Adjustment Voucher: <span className="text-primary ms-2">{doc.voucher_no}</span>
                                            </h4>
                                            <small className="text-muted">
                                                Created on {moment(doc.createdAt).format("DD/MM/YYYY hh:mm A")}
                                            </small>
                                        </div>
                                        <div className="d-flex gap-2 mt-2 mt-md-0">
                                            <button
                                                className="btn btn-outline-secondary d-flex align-items-center"
                                                onClick={() => navigate("/main-store/user/stock-adjustment-management")}
                                            >
                                                <ArrowLeft size={16} className="me-1" />
                                                Back to List
                                            </button>
                                            <button
                                                className="btn btn-primary d-flex align-items-center"
                                                onClick={handleDownloadPdf}
                                            >
                                                <Download size={16} className="me-1" />
                                                Download Slip (PDF)
                                            </button>
                                        </div>
                                    </div>
                                    <div className="card-body">
                                        <div className="row">
                                            <ReadOnlyField
                                                label="Adjustment Date"
                                                value={moment(doc.trans_date).format("DD/MM/YYYY")}
                                            />
                                            <ReadOnlyField
                                                label="Adjustment Direction"
                                                value={isPlus ? "+ Stock Inward (Surplus/Gain)" : "- Stock Outward (Loss/Unrecorded Issue)"}
                                                highlightColor={isPlus ? "#198754" : "#dc3545"}
                                            />
                                            <ReadOnlyField
                                                label="Voucher Status"
                                                value={
                                                    doc.status === 3 ? "APPROVED" :
                                                    doc.status === 4 ? "REJECTED" : "PENDING APPROVAL"
                                                }
                                                highlightColor={
                                                    doc.status === 3 ? "#198754" :
                                                    doc.status === 4 ? "#dc3545" : "#ffc107"
                                                }
                                            />
                                            <ReadOnlyField
                                                label="Project Allocation"
                                                value={doc.project_id ? `${doc.project_id.project_no} - ${doc.project_id.name}` : "General (No Project)"}
                                            />
                                            <ReadOnlyField
                                                label="Store Location"
                                                value={doc.store_location_id?.name || "General Store"}
                                            />
                                            <ReadOnlyField
                                                label="Primary Reason Code"
                                                value={doc.adjustment_reason || "Physical vs System Variance"}
                                            />
                                            <ReadOnlyField
                                                label="Prepared By"
                                                value={doc.admin_id?.name || doc.admin_id?.email || "Store Keeper"}
                                            />
                                            <ReadOnlyField
                                                label="Approved / Verified By"
                                                value={doc.verify_by?.user_id?.name || (doc.status === 3 ? "Authorized Signatory" : "Awaiting Approval")}
                                            />
                                            {doc.remarks && (
                                                <div className="col-12">
                                                    <div className="p-2 border rounded bg-light">
                                                        <small className="text-muted d-block text-uppercase" style={{ fontSize: '10px' }}>
                                                            General Remarks / Justification
                                                        </small>
                                                        <div className="mt-1 text-dark">{doc.remarks}</div>
                                                    </div>
                                                </div>
                                            )}
                                            {doc.verify_by?.reject_reason && (
                                                <div className="col-12 mt-2">
                                                    <div className="p-2 border rounded bg-light-danger border-danger">
                                                        <small className="text-danger d-block font-weight-bold">
                                                            Rejection Reason
                                                        </small>
                                                        <div className="mt-1 text-danger">{doc.verify_by.reject_reason}</div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Items Card */}
                                <div className="card">
                                    <div className="card-header bg-white py-3">
                                        <h5 className="card-title mb-0 font-weight-bold">
                                            Adjusted Items Breakdown ({items.length})
                                        </h5>
                                    </div>
                                    <div className="card-body p-0">
                                        <div className="table-responsive">
                                            <table className="table border-0 mb-0 custom-table table-striped comman-table">
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: "4%" }} className="text-center">#</th>
                                                        <th>Material Item & Description</th>
                                                        <th style={{ width: "8%" }} className="text-center">UOM</th>
                                                        <th style={{ width: "12%" }} className="text-end">System Qty</th>
                                                        <th style={{ width: "12%" }} className="text-end">Physical Count</th>
                                                        <th style={{ width: "12%" }} className="text-end">Adjusted Qty</th>
                                                        <th style={{ width: "10%" }} className="text-end">Unit Rate (₹)</th>
                                                        <th style={{ width: "12%" }} className="text-end">Total Amount (₹)</th>
                                                        <th style={{ width: "20%" }}>Item Reason / Remarks</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {items.map((row, idx) => (
                                                        <tr key={idx}>
                                                            <td className="text-center">{idx + 1}</td>
                                                            <td>
                                                                <span className="font-weight-bold text-dark">
                                                                    {row.item_id?.name || row.item_name || "Material Item"}
                                                                </span>
                                                                {(row.item_id?.code || row.item_code) && (
                                                                    <span className="badge badge-soft-secondary font-weight-normal ms-2">
                                                                        {row.item_id?.code || row.item_code}
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td className="text-center">
                                                                <span className="badge badge-soft-info">{resolveUnit(row)}</span>
                                                            </td>
                                                            <td className="text-end text-muted">{parseFloat(row.system_qty || 0).toFixed(2)}</td>
                                                            <td className="text-end font-weight-bold">{parseFloat(row.physical_qty || 0).toFixed(2)}</td>
                                                            <td
                                                                className="text-end font-weight-bold"
                                                                style={{ color: isPlus ? "#198754" : "#dc3545" }}
                                                            >
                                                                {isPlus ? "+" : "-"}{parseFloat(row.quantity || 0).toFixed(2)}
                                                            </td>
                                                            <td className="text-end">₹{parseFloat(row.rate || 0).toFixed(2)}</td>
                                                            <td className="text-end font-weight-bold">
                                                                ₹{parseFloat(row.total_amount || (row.quantity * row.rate) || 0).toFixed(2)}
                                                            </td>
                                                            <td>
                                                                <small className="text-muted">{row.adjustment_reason || row.remarks || doc.adjustment_reason || "-"}</small>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                                <tfoot className="table-light font-weight-bold">
                                                    <tr>
                                                        <td colSpan="5" className="text-end">Net Adjusted Summary:</td>
                                                        <td className="text-end" style={{ color: isPlus ? "#198754" : "#dc3545" }}>
                                                            {isPlus ? "+" : "-"}{totalQty.toFixed(2)}
                                                        </td>
                                                        <td></td>
                                                        <td className="text-end text-primary fs-6">₹{totalValuation.toFixed(2)}</td>
                                                        <td></td>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <Loader />
                    )}
                    {/* Generous bottom spacing */}
                    <div style={{ height: "45px" }} />
                </div>
            </div>
            {/* Print stylesheet */}
            <style>{`
                @media print {
                    .header, .sidebar, .sidebar-overlay, .footer, .btn, .breadcrumb-bar {
                        display: none !important;
                    }
                    .page-wrapper {
                        margin: 0 !important;
                        padding: 0 !important;
                    }
                    .card {
                        border: 1px solid #000 !important;
                        box-shadow: none !important;
                        margin-bottom: 12px !important;
                    }
                    .table th, .table td {
                        border: 1px solid #000 !important;
                        color: #000 !important;
                    }
                }
            `}</style>
            <Footer />
        </div>
    );
};

export default ViewStockAdjustment;
